import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { InvoicesService } from '../../../services/invoices.service';
import { ErrorPanelService } from '../../../services/error-panel.service';
import { Subject } from 'rxjs';
import { TipologiaCassaCode } from '../../../models/invoices';
import { ConfirmationPanelComponent } from '../../../shared/confirmation-panel/confirmation-panel.component';
import { MatDialog } from '@angular/material/dialog';

export enum IvaOption {
  IVA_22 = '22%',
  IVA_10 = '10%',
  IVA_5 = '5%',
  IVA_4 = '4%',
  IVA_0_N1 = '0% - N1',
  IVA_0_N2_1 = '0% - N2.1',
  IVA_0_N2_2 = '0% - N2.2',
  IVA_0_N4 = '0% - N4',
}
export enum IvaOptionWithDescription {
  IVA_22 = '22%',
  IVA_10 = '10%',
  IVA_5 = '5%',
  IVA_4 = '4%',
  IVA_0_N1 = '0% - N1: escluse ex art.15',
  IVA_0_N2_1 = '0% - N2.1: non soggette ad IVA ai sensi degli artt. da 7 a 7-septies del DPR 633/72',
  IVA_0_N2_2 = '0% - N2.2: non soggette - altri casi',
  IVA_0_N4 = '0% - N4: esenti',
}
@Injectable({
  providedIn: 'root',
})
export class CreateModifyInvoicesService {
  displayedColumns = [
    'descrizione',
    'quantita',
    'importo €',
    'iva',
    'totale',
    '',
  ];

  private isDialogOpen = false;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private invoicesService: InvoicesService,
    private errorPanelService: ErrorPanelService
  ) {
    // Subscribe to dialog state changes
    this.dialog.afterAllClosed.subscribe(() => {
      this.isDialogOpen = false;
    });
  }

  private openDialog(title: string, infoText: string): void {
    if (this.isDialogOpen) return;

    this.isDialogOpen = true;
    this.dialog.open(ConfirmationPanelComponent, {
      width: '400px',
      data: { title, infoText },
    });
  }

  private startWithImpostaDiBolloValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) return { required: true };
      if (!value.startsWith('Imposta di Bollo')) {
        this.openDialog(
          'Errore nella linea del bollo',
          "La linea del bollo deve iniziare con 'Imposta di Bollo'."
        );
        control.setValue('Imposta di Bollo');
        // return { startWithImpostaBollo: true };
      }
      return null;
    };
  }

  createInvoiceForm(): FormGroup {
    return this.fb.group({
      tipo: ['', Validators.required],
      client_id: [{ value: null, disabled: true }, Validators.required],
      cliente: [{ value: null, disabled: true }, Validators.required],
      riferimento: [
        { value: null, disabled: true },
        [Validators.maxLength(200)],
      ],
      progressivo_numero: [
        { value: null, disabled: true },
        [Validators.required, Validators.pattern(/^\d{3}$/)],
      ],
      progressivo_anno: [{ value: null, disabled: true }, Validators.required],
      data: [{ value: null, disabled: true }, Validators.required],
      pagata: [{ value: false, disabled: true }],
      scadenza: [{ value: null, disabled: true }, Validators.required],
      pagamento: [{ value: null, disabled: true }, Validators.required],
      cassa_previdenza: [{ value: true, disabled: true }],
      tipologia_cassa: [{ value: null, disabled: true }],
      cassa_previdenza_codice_iva: [{ value: null, disabled: true }],
      percentuale_cassa: [{ value: null, disabled: true }],
      escludi_bollo: [{ value: false, disabled: true }],
      applica_bollo: [{ value: false, disabled: true }],
      imponibile: [{ value: null, disabled: true }],
      totale_cassa_previdenza: [{ value: null, disabled: true }],
      imposta: [{ value: 0, disabled: true }],
      bollo: [{ value: null, disabled: true }],
      totale: [{ value: null, disabled: true }],
      linee_documento: this.fb.array([]),
      doctor_bank_name: [{ value: null, disabled: true }],
      doctor_bank_iban: [{ value: null, disabled: true }],
      numero_fattura_originale: [{ value: null, disabled: true }], // FIXME: when a draft sts expense is issued, the frontend also passes numero_fattura_original=null. this field should not be present in the bod of the request.
      importo: [{ value: null, disabled: true }],
      flag_opposizione: [{ value: false, disabled: true }],
      totale_ritenuta: [{ value: null, disabled: true }],
    });
  }

  createDocumentLine(): FormGroup {
    return this.fb.group({
      descrizione: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(1000),
        ],
      ],
      quantita: ['1.00'],
      prezzo: [
        '0.00',
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      sconto: [
        '0',
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      iva_option: [''],
      totale: [{ value: '0.00', disabled: true }],
    });
  }

  createBolloLine(defaultIvaOption?: IvaOption): FormGroup {
    return this.fb.group({
      descrizione: [
        { value: 'Imposta di Bollo', disabled: false },
        [Validators.required, this.startWithImpostaDiBolloValidator()],
      ],
      quantita: [{ value: '1.00', disabled: true }],
      prezzo: [
        { value: '2.00', disabled: true },
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      sconto: [
        { value: '0', disabled: true },
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      iva_option: [
        { value: defaultIvaOption || IvaOption.IVA_0_N1, disabled: false },
      ],
      totale: [{ value: '2.00', disabled: true }],
    });
  }

  calculateLineTotals(lineGroup: FormGroup): number {
    const quantita = parseFloat(lineGroup.get('quantita')?.value || '0');
    const prezzo = parseFloat(lineGroup.get('prezzo')?.value || '0');
    const sconto = parseFloat(lineGroup.get('sconto')?.value || '0');

    return (quantita * prezzo * (100 - sconto)) / 100;
  }

  formatDisplayNumber(value: string): string {
    if (!value) return '';
    return value.replace('/SDI', '');
  }

  calculateInvoiceTotals(
    lines: FormGroup[],
    cassaPrevidenzaEnabled: boolean,
    percentualeCassa: number,
    regimeFiscale: string | undefined,
    escludi_bollo: boolean,
    applica_bollo: boolean,
    invoice_tipo: string | undefined,
    tipologiaCassa?: string | null
  ): {
    imponibile: number;
    imponibileNettoBollo: number;
    cassaTaxableBase: number;
    totaleCassaPrevidenza: number;
    imposta: number;
    bollo: number;
    totale: number;
    totaleRitenuta: number;
  } {
    // Find bollo line item if it exists
    const bolloLine = lines.find((line) =>
      line.get('descrizione')?.value.startsWith('Imposta di Bollo')
    );

    // Calculate base imponibile from line totals
    const imponibile = lines.reduce((sum, line) => {
      if (
        line.get('descrizione')?.value.startsWith('Imposta di Bollo') &&
        regimeFiscale &&
        regimeFiscale !== 'RF19'
      ) {
        return sum;
      }
      return sum + parseFloat(line.get('totale')?.value || '0');
    }, 0);

    const bolloLineValue = bolloLine
      ? parseFloat(bolloLine.get('totale')?.value || '0')
      : 0;

    const bollo = bolloLineValue;
    let imponibileNettoBollo = imponibile;

    if (regimeFiscale === 'RF19' && !escludi_bollo && applica_bollo) {
      imponibileNettoBollo = imponibile - 2;
    }

    // ENPAP (TC21) applies the contribution on taxable amount + stamp duty.
    const cassaTaxableBase =
      tipologiaCassa === TipologiaCassaCode.TC21
        ? imponibileNettoBollo + bollo
        : imponibileNettoBollo;

    // Calculate cassa previdenza
    const totaleCassaPrevidenza = cassaPrevidenzaEnabled
      ? (cassaTaxableBase * percentualeCassa) / 100
      : 0;

    let totaleRitenuta = 0;
    if (
      regimeFiscale === 'RF01' &&
      (invoice_tipo === 'Parcella SDI' ||
        invoice_tipo === 'Nota di Credito SDI')
    ) {
      if (bolloLine && bolloLine.get('iva_option')?.value.includes('N4')) {
        totaleRitenuta = (imponibile + bollo) * 0.2;
      } else {
        totaleRitenuta = imponibileNettoBollo * 0.2;
      }
    }
    // Calculate final total
    let totale = imponibileNettoBollo + totaleCassaPrevidenza + bollo;
    if (totaleRitenuta > 0) {
      totale = totale - totaleRitenuta;
    }

    return {
      imponibile,
      imponibileNettoBollo,
      cassaTaxableBase,
      totaleCassaPrevidenza,
      imposta: 0, // This will be calculated separately
      bollo,
      totale,
      totaleRitenuta,
    };
  }

  calculateImposta(
    lines: FormGroup[],
    cassaPrevidenzaEnabled: boolean,
    cassaPrevidenzaCodiceIva: string | null,
    percentualeCassa: number,
    cassaTaxableBase: number
  ): number {
    let imposta = lines.reduce((sum, line) => {
      if (line.get('descrizione')?.value.startsWith('Imposta di Bollo')) {
        return sum;
      }

      const totale = parseFloat(line.get('totale')?.value || '0');
      const ivaOption = line.get('iva_option')?.value || '0%';
      const ivaPercentage = parseFloat(ivaOption.split('%')[0]) || 0;

      return sum + (totale * ivaPercentage) / 100;
    }, 0);

    if (cassaPrevidenzaEnabled && cassaPrevidenzaCodiceIva) {
      imposta +=
        (parseFloat(cassaPrevidenzaCodiceIva.split('%')[0]) *
          cassaTaxableBase *
          percentualeCassa) /
        100 /
        100;
    }

    return imposta;
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((c) => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          } else {
            c.markAsTouched();
          }
        });
      }
    });
  }

  handleBolloLine(
    documentLinesArray: FormArray,
    escludi_bollo: boolean,
    invoiceForm: FormGroup,
    defaultIvaOption?: IvaOption,
    regimeFiscale?: string
  ) {
    const bolloLineIndex = documentLinesArray.controls.findIndex((control) =>
      control.get('descrizione')?.value?.startsWith('Imposta di Bollo')
    );
    const bolloLineIVAOption = documentLinesArray
      .at(bolloLineIndex)
      ?.get('iva_option')?.value;

    const totalWithoutBolloAndWithoutIVA = documentLinesArray.controls.reduce(
      (sum, line, index) => {
        if (
          index !== bolloLineIndex &&
          line.get('iva_option')?.value.includes('0% -')
        ) {
          return sum + parseFloat(line.get('totale')?.value || '0');
        }
        return sum;
      },
      0
    );

    if (totalWithoutBolloAndWithoutIVA > 77.47) {
      invoiceForm.get('applica_bollo')?.setValue(true);
    } else {
      invoiceForm.get('applica_bollo')?.setValue(false);
    }

    if (totalWithoutBolloAndWithoutIVA > 77.47 && !escludi_bollo) {
      if (bolloLineIndex === -1) {
        const bolloLine = this.createBolloLine(
          bolloLineIVAOption || defaultIvaOption
        );
        documentLinesArray.push(bolloLine);
        this.setupLineCalculations(bolloLine, () => {
          this.updateFormWithCalculatedTotals(
            documentLinesArray,
            invoiceForm,
            regimeFiscale
          );
        });
      }
    } else {
      if (bolloLineIndex !== -1) {
        documentLinesArray.removeAt(bolloLineIndex);
      }
    }
  }

  updateBolloLine(
    documentLinesArray: FormArray,
    modifiedBolloLineNaturaIVA: IvaOption,
    modifiedDescrizioneBolloLine?: string
  ) {
    const bolloLineIndex = documentLinesArray.controls.findIndex((control) =>
      control.get('descrizione')?.value.startsWith('Imposta di Bollo')
    );
    if (bolloLineIndex !== -1) {
      documentLinesArray
        .at(bolloLineIndex)
        .get('iva_option')
        ?.setValue(modifiedBolloLineNaturaIVA);
      if (modifiedDescrizioneBolloLine) {
        documentLinesArray
          .at(bolloLineIndex)
          .get('descrizione')
          ?.setValue(modifiedDescrizioneBolloLine);
      }
    }
  }

  setupLineCalculations(lineGroup: FormGroup, onCalculate: () => void) {
    const calculateTotal = () => {
      const totale = this.calculateLineTotals(lineGroup);
      lineGroup
        .get('totale')
        ?.setValue(totale.toFixed(2), { emitEvent: false });
      onCalculate();
    };

    ['quantita', 'prezzo', 'sconto', 'iva_option'].forEach((field) => {
      lineGroup.get(field)?.valueChanges.subscribe(() => calculateTotal());
    });

    calculateTotal();
  }

  removeDocumentLine(
    index: number,
    documentLinesArray: FormArray,
    invoiceForm: FormGroup
  ) {
    const line = documentLinesArray.at(index);
    if (line.get('descrizione')?.value?.startsWith('Imposta di Bollo')) {
      invoiceForm.get('escludi_bollo')?.setValue(true);
    } else {
      documentLinesArray.removeAt(index);
      this.handleBolloLine(
        documentLinesArray,
        invoiceForm.get('escludi_bollo')?.value || false,
        invoiceForm
      );
    }
  }

  addDocumentLine(
    documentLinesArray: FormArray,
    fieldsDisabled: boolean,
    defaultIvaOption: IvaOption,
    onCalculate: () => void
  ) {
    const newLine = this.fb.group({
      descrizione: [
        { value: '', disabled: fieldsDisabled },
        Validators.required,
      ],
      quantita: [{ value: '1.00', disabled: fieldsDisabled }],
      prezzo: [
        { value: '0.00', disabled: fieldsDisabled },
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      sconto: [
        { value: '0', disabled: fieldsDisabled },
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      iva_option: [{ value: defaultIvaOption, disabled: fieldsDisabled }],
      totale: [{ value: '0.00', disabled: true }],
    });

    this.setupLineCalculations(newLine, onCalculate);
    documentLinesArray.push(newLine);
  }

  updateFormWithCalculatedTotals(
    documentLinesArray: FormArray,
    invoiceForm: FormGroup,
    regimeFiscale: string | undefined
  ) {
    const cassaPrevidenzaEnabled = invoiceForm.get('cassa_previdenza')?.value;
    const percentualeCassa = parseFloat(
      invoiceForm.get('percentuale_cassa')?.value || '0'
    );
    const escludi_bollo = invoiceForm.get('escludi_bollo')?.value;
    const applica_bollo = invoiceForm.get('applica_bollo')?.value;
    const totals = this.calculateInvoiceTotals(
      documentLinesArray.controls as FormGroup[],
      cassaPrevidenzaEnabled,
      percentualeCassa,
      regimeFiscale,
      escludi_bollo,
      applica_bollo,
      invoiceForm.get('tipo')?.value,
      invoiceForm.get('tipologia_cassa')?.value
    );

    // Handle cassa previdenza
    if (!cassaPrevidenzaEnabled) {
      invoiceForm.patchValue(
        {
          totale_cassa_previdenza: '0.00',
        },
        { emitEvent: false }
      );
    }

    // Calculate imposta separately since it needs cassa_previdenza_codice_iva
    const cassaPrevidenzaCodiceIva = invoiceForm.get(
      'cassa_previdenza_codice_iva'
    )?.value;
    const imposta = this.calculateImposta(
      documentLinesArray.controls as FormGroup[],
      cassaPrevidenzaEnabled,
      cassaPrevidenzaCodiceIva,
      percentualeCassa,
      totals.cassaTaxableBase
    );

    // Update form with calculated values
    invoiceForm.patchValue(
      {
        imponibile: totals.imponibile.toFixed(2),
        totale_cassa_previdenza: totals.totaleCassaPrevidenza.toFixed(2),
        imposta: imposta.toFixed(2),
        bollo: totals.bollo.toFixed(2),
        totale: (totals.totale + imposta).toFixed(2),
        totale_ritenuta: totals.totaleRitenuta.toFixed(2),
      },
      { emitEvent: false }
    );
  }
}
